/**
 * `SeriesChart` — design §4 step 9's "time series of NDVI composites and CHIRPS months for
 * the selected unit" (`GET uc3/series`). Two small panels in one SVG: NDVI as a line, rain
 * as bars — each on its own scale, since the two are different units and neither series is
 * meant to be read against the other's axis.
 */
import { fmtNum } from '../../../lib/format';

export const SR_W = 280;
export const SR_PANEL_H = 70;
export const SR_GAP = 14;
export const SR_PAD = 8;

export interface SeriesChartProps {
  ndvi: { date: string; mean: number }[];
  rain: { month: string; total: number }[];
  ndviLabel: string;
  rainLabel: string;
}

function linePath(values: number[], w: number, h: number, pad: number): string {
  if (!values.length) return '';
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = values.length > 1 ? (w - 2 * pad) / (values.length - 1) : 0;
  return values
    .map((v, i) => {
      const x = pad + i * step;
      const y = h - pad - ((v - min) / span) * (h - 2 * pad);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

export function SeriesChart({ ndvi, rain, ndviLabel, rainLabel }: SeriesChartProps) {
  const h = SR_PANEL_H * 2 + SR_GAP;
  const ndviValues = ndvi.map((p) => p.mean);
  const rainValues = rain.map((p) => p.total);
  const maxRain = Math.max(1, ...rainValues);
  const rainW = rain.length ? (SR_W - 2 * SR_PAD) / rain.length : 0;

  const caption =
    `${ndviLabel}: ${ndvi.map((p) => `${p.date} ${fmtNum(p.mean, 0)}`).join(', ') || '—'}. ` +
    `${rainLabel}: ${rain.map((p) => `${p.month} ${fmtNum(p.total, 0)}mm`).join(', ') || '—'}.`;

  return (
    <svg
      className="intel-chart sr-chart"
      viewBox={`0 0 ${SR_W} ${h}`}
      role="img"
      aria-label={caption}
      data-testid="series-chart"
    >
      <g data-testid="series-ndvi">
        <text x={SR_PAD} y={12} className="sr-panel-label">
          {ndviLabel}
        </text>
        {ndviValues.length ? (
          <path d={linePath(ndviValues, SR_W, SR_PANEL_H, SR_PAD)} fill="none" stroke="var(--brand)" strokeWidth={1.6} />
        ) : null}
        {ndvi.map((p, i) => {
          const step = ndviValues.length > 1 ? (SR_W - 2 * SR_PAD) / (ndviValues.length - 1) : 0;
          const min = Math.min(...ndviValues);
          const max = Math.max(...ndviValues);
          const span = max - min || 1;
          const x = SR_PAD + i * step;
          const y = SR_PANEL_H - SR_PAD - ((p.mean - min) / span) * (SR_PANEL_H - 2 * SR_PAD);
          return <circle key={p.date} cx={x} cy={y} r={2.4} fill="var(--brand)" />;
        })}
      </g>
      <g transform={`translate(0, ${SR_PANEL_H + SR_GAP})`} data-testid="series-rain">
        <text x={SR_PAD} y={12} className="sr-panel-label">
          {rainLabel}
        </text>
        {rain.map((p, i) => {
          const barH = (p.total / maxRain) * (SR_PANEL_H - 2 * SR_PAD - 12);
          const x = SR_PAD + i * rainW;
          const y = SR_PANEL_H - SR_PAD - barH;
          return (
            <rect key={p.month} x={x + 2} y={y} width={Math.max(2, rainW - 4)} height={Math.max(0, barH)} fill="var(--blue)" />
          );
        })}
      </g>
    </svg>
  );
}

export default SeriesChart;
